/**
 * Servicio para integración con Vercel API
 * Maneja logs, deployments y métricas de salud de la aplicación
 */
class VercelService {
  constructor(session = null) {
    this.baseURL = 'https://api.vercel.com';
    this.token = process.env.REACT_APP_VERCEL_TOKEN;
    this.teamId = process.env.REACT_APP_VERCEL_TEAM_ID;
    this.projectId = process.env.REACT_APP_VERCEL_PROJECT_ID;
    this.maxLogsPerRequest = parseInt(process.env.MAX_LOGS_PER_REQUEST) || 100;
    this.session = session;
  }

  /**
   * Headers para autenticación con Vercel API
   */
  getHeaders() {
    return {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Obtiene logs de Vercel en tiempo real
   */
  async getLogs(options = {}) {
    try {
      if (!this.isConfigured()) {
        return this.getMockLogs();
      }
      const {
        limit = this.maxLogsPerRequest,
        since = null,
        until = null,
        level = null
      } = options;

      let url = `${this.baseURL}/v2/deployments/${this.projectId}/events`;
      const params = new URLSearchParams();
      
      if (limit) params.append('limit', limit.toString());
      if (since) params.append('since', since);
      if (until) params.append('until', until);
      if (level) params.append('level', level);
      if (this.teamId) params.append('teamId', this.teamId);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo logs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return this.formatLogs(data);
    } catch (error) {
      console.error('Error obteniendo logs de Vercel:', error);
      return this.getMockLogs(); // Fallback a logs mock si falla la API
    }
  }

  /**
   * Obtiene información de deployments
   */
  async getDeployments(limit = 20) {
    try {
      if (!this.isConfigured()) {
        return this.getMockDeployments();
      }
      let url = `${this.baseURL}/v6/deployments`;
      const params = new URLSearchParams();
      
      if (this.projectId) params.append('projectId', this.projectId);
      if (this.teamId) params.append('teamId', this.teamId);
      if (limit) params.append('limit', limit.toString());

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo deployments: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return this.formatDeployments(data.deployments || []);
    } catch (error) {
      console.error('Error obteniendo deployments de Vercel:', error);
      return this.getMockDeployments(); // Fallback a deployments mock
    }
  }

  /**
   * Obtiene métricas de performance
   */
  async getPerformanceMetrics(timeframe = '1d') {
    try {
      if (!this.isConfigured()) {
        return this.getMockPerformanceMetrics();
      }
      const url = `${this.baseURL}/v1/analytics/usage`;
      const params = new URLSearchParams();
      
      if (this.projectId) params.append('projectId', this.projectId);
      if (this.teamId) params.append('teamId', this.teamId);
      params.append('period', timeframe);

      const response = await fetch(`${url}?${params.toString()}`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo métricas: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return this.formatPerformanceMetrics(data);
    } catch (error) {
      console.error('Error obteniendo métricas de performance:', error);
      return this.getMockPerformanceMetrics(); // Fallback a métricas mock
    }
  }

  /**
   * Obtiene estado de salud de la aplicación
   * Alias para getHealthStatus para compatibilidad
   */
  async getAppHealth() {
    return this.getHealthStatus();
  }

  /**
   * Obtiene estado de salud de la aplicación
   */
  async getHealthStatus() {
    try {
      const [deployments, logs] = await Promise.all([
        this.getDeployments(5),
        this.getLogs({ limit: 50, level: 'error' })
      ]);

      const latestDeployment = deployments[0];
      const errorLogs = logs.filter(log => log.level === 'error');
      const recentErrors = errorLogs.filter(log => {
        const logTime = new Date(log.timestamp);
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        return logTime > oneHourAgo;
      });

      // Determinar estado de salud
      let status = 'healthy';
      let message = 'Aplicación funcionando correctamente';

      if (latestDeployment?.state === 'ERROR') {
        status = 'critical';
        message = 'Último deployment falló';
      } else if (recentErrors.length > 10) {
        status = 'warning';
        message = `${recentErrors.length} errores en la última hora`;
      } else if (recentErrors.length > 0) {
        status = 'warning';
        message = `${recentErrors.length} errores recientes detectados`;
      }

      return {
        status,
        message,
        lastDeployment: latestDeployment,
        errorCount: recentErrors.length,
        uptime: this.calculateUptime(deployments),
        lastChecked: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error obteniendo estado de salud:', error);
      return {
        status: 'unknown',
        message: 'No se pudo determinar el estado de salud',
        lastChecked: new Date().toISOString()
      };
    }
  }

  /**
   * Formatea logs para el dashboard
   */
  formatLogs(data) {
    if (!data || !Array.isArray(data)) return [];

    return data.map(log => ({
      id: log.id || Math.random().toString(36),
      timestamp: log.timestamp || new Date().toISOString(),
      level: log.level || 'info',
      message: log.text || log.message || 'Sin mensaje',
      source: log.source || 'vercel',
      deployment: log.deploymentId || 'unknown'
    }));
  }

  /**
   * Formatea deployments para el dashboard
   */
  formatDeployments(deployments) {
    return deployments.map(deployment => ({
      id: deployment.uid,
      url: deployment.url,
      state: deployment.state,
      createdAt: deployment.createdAt,
      readyState: deployment.readyState,
      target: deployment.target || 'production',
      environment: deployment.target || 'production',
      projectName: deployment.name || deployment.project?.name || this.projectId || 'Proyecto',
      projectId: deployment.projectId || this.projectId || null,
      creator: deployment.creator?.username || 'unknown',
      duration: deployment.buildingAt && deployment.readyAt ? 
        new Date(deployment.readyAt) - new Date(deployment.buildingAt) : null
    }));
  }

  /**
   * Formatea métricas de performance
   */
  formatPerformanceMetrics(data) {
    return {
      requests: data?.requests || 0,
      bandwidth: data?.bandwidth || 0,
      errors: data?.errors || 0,
      responseTime: data?.responseTime || 0,
      uptime: data?.uptime || 99.9
    };
  }

  /**
   * Calcula uptime basado en deployments
   */
  calculateUptime(deployments) {
    if (!deployments || deployments.length === 0) return 99.9;

    const successfulDeployments = deployments.filter(d => d.state === 'READY').length;
    const totalDeployments = deployments.length;
    
    return totalDeployments > 0 ? (successfulDeployments / totalDeployments) * 100 : 99.9;
  }

  /**
   * Logs mock para fallback
   */
  getMockLogs() {
    const levels = ['info', 'warn', 'error'];
    const messages = [
      'Application started successfully',
      'Database connection established',
      'User authentication completed',
      'API request processed',
      'Cache updated',
      'Warning: High memory usage detected',
      'Error: Database connection timeout',
      'Info: Scheduled task completed'
    ];

    return Array.from({ length: 20 }, (_, i) => ({
      id: `mock-${i}`,
      timestamp: new Date(Date.now() - i * 60000).toISOString(),
      level: levels[Math.floor(Math.random() * levels.length)],
      message: messages[Math.floor(Math.random() * messages.length)],
      source: 'vercel',
      deployment: 'mock-deployment'
    }));
  }

  /**
   * Deployments mock para fallback
   */
  getMockDeployments() {
    const states = ['READY', 'BUILDING', 'ERROR'];
    
    return Array.from({ length: 5 }, (_, i) => ({
      id: `mock-deployment-${i}`,
      url: `https://tadmin-${i}.vercel.app`,
      state: i === 0 ? 'READY' : states[Math.floor(Math.random() * states.length)],
      createdAt: new Date(Date.now() - i * 3600000).toISOString(),
      readyState: 'READY',
      target: 'production',
      environment: 'production',
      projectName: 'tadmin',
      projectId: this.projectId || 'tadmin',
      creator: 'developer',
      duration: 45000 + Math.random() * 30000
    }));
  }

  /**
   * Métricas mock para fallback
   */
  getMockPerformanceMetrics() {
    return {
      requests: Math.floor(Math.random() * 10000) + 1000,
      bandwidth: Math.floor(Math.random() * 1000000) + 100000,
      errors: Math.floor(Math.random() * 50),
      responseTime: Math.floor(Math.random() * 500) + 100,
      uptime: 99.9 - Math.random() * 0.5
    };
  }

  /**
   * Obtiene estado del entorno y configuración
   */
  async getEnvironmentStatus() {
    try {
      const config = this.getConfig();
      const isHealthy = config.isConfigured;
      
      return {
        status: isHealthy ? 'healthy' : 'warning',
        message: isHealthy ? 'Entorno configurado correctamente' : 'Faltan variables de entorno',
        details: {
          vercelToken: config.hasToken ? 'Configurado' : 'Faltante',
          projectId: config.hasProjectId ? 'Configurado' : 'Faltante',
          teamId: config.hasTeamId ? 'Configurado' : 'Opcional',
        },
        lastChecked: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error obteniendo estado del entorno:', error);
      return {
        status: 'error',
        message: 'Error verificando configuración del entorno',
        lastChecked: new Date().toISOString()
      };
    }
  }

  /**
   * Verifica si las credenciales están configuradas
   */
  isConfigured() {
    return !!(this.token && this.projectId);
  }

  /**
   * Obtiene configuración actual
   */
  getConfig() {
    return {
      hasToken: !!this.token,
      hasTeamId: !!this.teamId,
      hasProjectId: !!this.projectId,
      maxLogsPerRequest: this.maxLogsPerRequest,
      isConfigured: this.isConfigured()
    };
  }
}

export default VercelService;
